import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContentText, DialogTitle, Paper, Snackbar, TextField, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CustomAxios from '../utility/customAxios';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTE_PATHS } from '../routes/Routes';
import PasswordValidator from '../components/PasswordValidator';
import { externalFormStyling } from '../styles/sxStyling';
import { Loading } from '../uikit';
import _, { Dictionary } from 'lodash';

function InvalidTokenDialog({ open }: { open: boolean }) {
  const navigate = useNavigate();

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>
        Invalid reset token
      </DialogTitle>
      <DialogContentText>
        <Typography sx={{ mx: 2 }}>
          The password reset token you used is invalid.
          It may have expired or the reset link may be incorrect.
        </Typography>
        <Typography sx={{ mx: 2, mt: 2 }}>
          Please try reseting the password again or contact support if the issue persists.
        </Typography>
      </DialogContentText>
      <DialogActions sx={{ justifyContent: 'center', mt: 2 }}>
        <Button variant="contained" color="primary" onClick={() => navigate(ROUTE_PATHS.LOGIN)}>Ok</Button>
      </DialogActions>
    </Dialog>
  );
}

type TokenType = 'reset' | 'setup' | 'invalid';
type Copy = { [key in TokenType]: Dictionary<string> };
const COPY: Copy = {
  reset: {
    success: 'Your password has been reset!',
    button: 'Reset Password',
    title: 'Reset Password',
  },
  setup: {
    success: 'Your password has been set!',
    button: 'Set Password',
    title: 'Setup Your Password',
  },
  invalid: {},
};

export default function ResetPassword() {
  const [password, setPassword] = useState<string>('');
  const [verify, setVerify] = useState<string>('');
  const [error, setError] = useState<boolean>(true);
  const [reset, setReset] = useState<boolean>(false);
  const [tokenType, setTokenType] = useState<TokenType | null>(null);

  const { uuid } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    CustomAxios.get<TokenType>(`password/check-token/${uuid}`)
      .then((response) => setTokenType(response.data))
      .catch(() => setTokenType('invalid'));
  }, []);

  if (tokenType === null) return <Loading />;

  const copy = COPY[tokenType];

  const onSend = async () => {
    try {
      await CustomAxios.post('password/reset-password', { uuid, password });
      setReset(true);
      setTimeout(() => navigate(ROUTE_PATHS.LOGIN), 3 * 1000);
    } catch (e) {
      alert("There was an error resetting your password. Please try again or contact support.");
    }
  };

  const snackbarMessage = <Typography>
    {copy.success}<br />
    You will be redirected to the login page.
  </Typography>;

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '70vh',
        }}
      >
        <Typography component="h1" variant="h5">
          {copy.title}
        </Typography>
        <Box sx={externalFormStyling}>
          <Typography>Please enter your new password</Typography>
          <TextField
            autoFocus
            required
            label="New Password"
            type="password"
            value={password}
            onChange={event => setPassword(event.target.value)}
          />
          <TextField
            required
            label="Verify New Password"
            type="password"
            value={verify}
            onChange={event => setVerify(event.target.value)}
          />
          <PasswordValidator password={password} validatePassword={verify} validCallback={(passwordValid) => setError(!passwordValid)} />
          <Button fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} onClick={onSend} disabled={error}>
            {copy.button}
          </Button>
        </Box>
      </Box>
      <Snackbar open={reset} onClose={() => setReset(false)} message={snackbarMessage} />
      <InvalidTokenDialog open={tokenType === 'invalid'} />
    </Container>
  );
}